import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { compose, withStateHandlers } from 'recompose';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { Spinner } from 'components/core/Spinner';
import { Tabs, Tab } from 'components/core/Tabs';
import { H3, Paragraph } from 'components/core/Typography';
import { Link } from 'components/core/Link';
import { StorageUnits } from 'components/unit/StorageUnits';
import { VehicleUnits } from 'components/unit/VehicleUnits';
import { UnitSizeModal } from 'components/unit/UnitSizeModal';
import { UnitAmenityModal } from 'components/unit/UnitAmenityModal';
import { FacilityUnitMicrodata } from 'components/unit/FacilityUnitMicrodata';

import './FacilityUnitList.scss';

export const enhance = compose(
	withStateHandlers(
		{ expandedUnit: null, expandedUnitType: null },
		{
			onExpandUnit:
				({ expandedUnit, expandedUnitType }, { changeFormData }) =>
				(unitId, unitType) => {
					// reset move in date when changing unit type
					changeFormData('unit-reservation', 'moveInDate', null);
					return {
						expandedUnit:
							expandedUnit === unitId &&
							expandedUnitType === unitType
								? null
								: unitId,
						expandedUnitType: unitType,
					};
				},
		},
	),
	withShowHide('unitSizeModal'),
	withShowHide('unitAmenityModal'),
);

export function FacilityUnitList({
	expandedUnit,
	expandedUnitType,
	facility,
	onCreateBooking,
	onExpandUnit,
	storageUnits,
	vehicleUnits,
	unitSizeModalShowing,
	onShowUnitSizeModal,
	onHideUnitSizeModal,
	onShowUnitAmenityModal,
	onHideUnitAmenityModal,
	unitAmenityModalShowing,
	loading,
	isNewSearchPage,
}) {
	return (
		<div
			className={classnames('facility-unit-list', {
				'with-tabs': storageUnits.length && vehicleUnits.length,
			})}
		>
			<Tabs className="unit-type-tabs">
				{!!storageUnits.length && (
					<Tab
						title="Self-Storage"
						className="storage-tab"
					>
						<StorageUnits
							facility={facility}
							isNewSearchPage={isNewSearchPage}
							units={storageUnits}
							expandedUnit={
								expandedUnitType === 'storage'
									? expandedUnit
									: null
							}
							onExpandUnit={onExpandUnit}
							onCreateBooking={onCreateBooking}
							storageType="storage"
							onShowUnitSizeModal={onShowUnitSizeModal}
							onShowUnitAmenityModal={onShowUnitAmenityModal}
						/>
					</Tab>
				)}
				{!!vehicleUnits.length && (
					<Tab
						title="Vehicle Storage"
						className="vehicle-tab"
					>
						<VehicleUnits
							facility={facility}
							units={vehicleUnits}
							expandedUnit={
								expandedUnitType === 'vehicle'
									? expandedUnit
									: null
							}
							onExpandUnit={onExpandUnit}
							onCreateBooking={onCreateBooking}
							storageType="vehicle"
						/>
					</Tab>
				)}
			</Tabs>
			{isEmpty(storageUnits) && isEmpty(vehicleUnits) && (
				<div className="no-units">
					<H3
						size="medium"
						className="headline"
					>
						Available Units
					</H3>
					{loading && <Spinner />}
					{!loading && (
						<Fragment>
							<Paragraph>
								Sorry! There are no units available for this
								facility on SelfStorage.com at this time.
							</Paragraph>
							<Paragraph last>
								<Link
									secondary
									href={`/search?location=${facility.location.zip}&type=storage`}
								>
									Search SelfStorage.com for more storage
									units in this area.
								</Link>
							</Paragraph>
						</Fragment>
					)}
				</div>
			)}
			{!!storageUnits.length && (
				<div className="unit-modals">
					<UnitSizeModal
						showing={unitSizeModalShowing}
						onClose={onHideUnitSizeModal}
					/>
					<UnitAmenityModal
						showing={unitAmenityModalShowing}
						onClose={onHideUnitAmenityModal}
					/>
				</div>
			)}
			<FacilityUnitMicrodata
				storageUnits={storageUnits}
				vehicleUnits={vehicleUnits}
				facilityName={facility.name}
				facilityUrl={facility.url}
				facilityId={facility.id}
				facilityImageUrl={
					facility.images.length
						? `https://images.selfstorage.com/small-compress/${facility.images[0]}`
						: ''
				}
			/>
		</div>
	);
}

FacilityUnitList.propTypes = {
	expandedUnit: PropTypes.number,
	expandedUnitType: PropTypes.string,
	facility: PropTypes.object.isRequired,
	loading: PropTypes.bool,
	onExpandUnit: PropTypes.func.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	vehicleUnits: PropTypes.arrayOf(PropTypes.object),
	unitSizeModalShowing: PropTypes.bool,
	onShowUnitSizeModal: PropTypes.func,
	onHideUnitSizeModal: PropTypes.func,
	unitAmenityModalShowing: PropTypes.bool,
	onShowUnitAmenityModal: PropTypes.func,
	onHideUnitAmenityModal: PropTypes.func,
	isNewSearchPage: PropTypes.bool,
};

export default enhance(FacilityUnitList);
